import React, { useEffect } from 'react';
import { useUser } from '../../context/userContext';
import { Lock } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar, Button, Grid, Paper, Typography } from '@mui/material';
import { createStyles, makeStyles } from '@mui/styles';
import { useTranslation } from 'react-i18next';
import Loader from '../../component/Loader';
import { routesURL } from '../../routes/routesURL';
import { PasswordField, StandardField } from '../../component/form';
import { styled } from '@mui/material/styles';
type LoginScreenProps = {};

const useStyles = makeStyles(theme =>
  createStyles({
    background: {
      backgroundPosition: 'center',
      backgroundSize: 'cover',
      backgroundRepeat: 'no-repeat',
      width: '100vw',
      height: '100vh',
    },
    container: {
      padding: 16,
      width: 280,
      margin: '20px auto',
    },
    input: {
      marginBottom: 10,
    },
    loginButton: {
      margin: '24px 0',
    },
    headerContainer: {
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
      flexDirection: 'column',
    },
  }),
);

const StyledAvatar = styled(Avatar)(({ theme }) => ({
  backgroundColor: theme.palette.primary.main,
  color: theme.palette.primary.contrastText,
  marginBottom: 32,
}));

type FormValues = {
  login: string;
  password: string;
};

const LoginScreen: React.FC<LoginScreenProps> = () => {
  const { userState, signIn } = useUser();
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from?.pathname || routesURL.dashboard;

  const { handleSubmit, control } = useForm<FormValues>({
    defaultValues: {
      login: '',
      password: '',
    },
  });

  const onSubmit = async (data: FormValues) => {
    await signIn(data.login, data.password, 'morelonline.backoffice', () => {
      navigate(from, { replace: true });
    });
  };

  useEffect(() => {
    if (userState.isLogged) {
      navigate(from, { replace: true });
    }
  }, [from, navigate, userState.isLogged]);

  const classes = useStyles();
  const { t } = useTranslation();

  return (
    <div className={classes.background}>
      <Grid style={{ paddingTop: 20 }}>
        <Paper elevation={10} className={classes.container}>
          <Grid className={classes.headerContainer}>
            <StyledAvatar>
              <Lock />
            </StyledAvatar>
          </Grid>
          <form onSubmit={handleSubmit(onSubmit)}>
            <StandardField
              controllerInitialState={{
                name: 'login',
                control,
                rules: { required: t('requiredLogin') as string },
              }}
              label={t('login')}
            />
            <div style={{ marginTop: 8 }} />
            <PasswordField
              label={t('password')}
              controllerInitialState={{
                name: 'password',
                control,
                rules: { required: t('requiredPassword') as string },
              }}
            />
            {userState.error && <Typography>{userState.error.message}</Typography>}
            <Button type="submit" color="primary" variant="contained" className={classes.loginButton} fullWidth>
              <Loader display={userState.loading} displayText={false} size={20} />
              {!userState.loading && t('signIn')}
            </Button>
          </form>
        </Paper>
      </Grid>
    </div>
  );
};

export default LoginScreen;
